import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi, contractorsApi } from '../../api/client';
import { Card, CardHeader, CardTitle, CardBody } from '../../components/ui/Card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Input, { Select, Label } from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import { getRoleColor } from '../../lib/utils';
import { Plus, Edit, Key, UserX, UserCheck, Trash2, Check, X, Send } from 'lucide-react';

// 處長室 / 工安組 can view and manage all divisions and send LINE manually
const HQ_DIVISIONS = ['處長室', '工安組'];
const DIVISIONS = [...HQ_DIVISIONS, '南一段', '南二段', '南三段', '南四段', '南五段', '南六段'];

// Admin / 處長室 / 工安組 can always push LINE; contractors never
const pushIsBuiltIn = (role, division) => role === 'admin' || HQ_DIVISIONS.includes(division);
const canUserPush = (u) => pushIsBuiltIn(u.role, u.division) || (Boolean(u.canPushLine) && u.role !== 'contractor');

export default function UsersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [userModal, setUserModal] = useState({ open: false, user: null });
  const [passwordModal, setPasswordModal] = useState({ open: false, user: null });
  const [deactivateModal, setDeactivateModal] = useState({ open: false, user: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
  const [actionError, setActionError] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    fullName: '',
    division: '',
    role: 'section_user',
    contractorId: null,
    canPushLine: false,
    preferredLanguage: 'zh-TW',
  });
  const [formError, setFormError] = useState(null);

  // Fetch users
  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
    select: (res) => res.data,
  });

  const { data: contractors } = useQuery({
    queryKey: ['contractors', 'active'],
    queryFn: () => contractorsApi.getAll({ activeOnly: 'true' }),
    select: (res) => res.data,
  });

  const onMutationError = (error) => {
    setFormError(error.response?.data?.error || t('errors.generic', '操作失敗'));
  };

  // Create user mutation
  const createMutation = useMutation({
    mutationFn: (data) => usersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setUserModal({ open: false, user: null });
      resetForm();
    },
    onError: onMutationError,
  });

  // Update user mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => usersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setUserModal({ open: false, user: null });
      resetForm();
    },
    onError: onMutationError,
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }) => usersApi.resetPassword(id, password),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setPasswordModal({ open: false, user: null });
      setNewPassword('');
    },
  });

  // 停用 / 啟用 user
  const activeMutation = useMutation({
    mutationFn: ({ id, isActive }) => usersApi.update(id, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setDeactivateModal({ open: false, user: null });
    },
  });

  // Permanently delete user
  const deleteMutation = useMutation({
    mutationFn: (id) => usersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setDeleteModal({ open: false, user: null });
      setActionError(null);
    },
    onError: (error) => setActionError(error.response?.data?.error || t('errors.generic', '操作失敗')),
  });

  const closeDeleteModal = () => {
    setDeleteModal({ open: false, user: null });
    setActionError(null);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      fullName: '',
      division: '',
      role: 'section_user',
      contractorId: null,
      canPushLine: false,
      preferredLanguage: 'zh-TW',
    });
    setFormError(null);
  };

  const openCreateModal = () => {
    resetForm();
    setUserModal({ open: true, user: null });
  };

  const openEditModal = (user) => {
    setFormData({
      username: user.username,
      password: '',
      fullName: user.fullName,
      division: user.division || '',
      role: user.role,
      contractorId: user.contractorId || null,
      canPushLine: Boolean(user.canPushLine),
      preferredLanguage: user.preferredLanguage,
      isActive: Boolean(user.isActive),
    });
    setFormError(null);
    setUserModal({ open: true, user });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (formData.role === 'contractor' && !formData.contractorId) {
      setFormError(t('users.contractorRequired', '承攬商帳號必須指定承攬商'));
      return;
    }

    if (userModal.user) {
      // Update
      const { password, ...updateData } = formData;
      updateMutation.mutate({ id: userModal.user.id, data: updateData });
    } else {
      // Create
      createMutation.mutate(formData);
    }
  };

  const handleResetPassword = () => {
    if (newPassword.length < 6) return;
    resetPasswordMutation.mutate({
      id: passwordModal.user.id,
      password: newPassword,
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('users.title')}</h1>
        <Button onClick={openCreateModal}>
          <Plus className="w-4 h-4 mr-2" />
          {t('users.newUser')}
        </Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-tpc-blue"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('auth.username')}</TableHead>
                  <TableHead>{t('users.fullName')}</TableHead>
                  <TableHead>{t('reports.division')}</TableHead>
                  <TableHead>{t('users.role')}</TableHead>
                  <TableHead>{t('users.linePush', 'LINE 推播')}</TableHead>
                  <TableHead>{t('users.isActive')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users?.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.username}</TableCell>
                    <TableCell>{user.fullName}</TableCell>
                    <TableCell>{user.role === 'contractor' ? (user.contractorName || '-') : (user.division || '-')}</TableCell>
                    <TableCell>
                      <Badge className={getRoleColor(user.role)}>
                        {t(`roles.${user.role}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {canUserPush(user) ? (
                        <Badge className="bg-green-100 text-green-800">
                          <Send className="w-3 h-3 mr-1" />
                          {t('users.canPush', '可推播')}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.isActive ? (
                        <Badge variant="success">
                          <Check className="w-3 h-3 mr-1" />
                          {t('users.active')}
                        </Badge>
                      ) : (
                        <Badge variant="danger">
                          <X className="w-3 h-3 mr-1" />
                          {t('users.inactive')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditModal(user)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPasswordModal({ open: true, user })}
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        {user.isActive ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            title={t('users.deactivate', '停用')}
                            onClick={() => setDeactivateModal({ open: true, user })}
                          >
                            <UserX className="w-4 h-4 text-amber-600" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            title={t('users.activate', '啟用')}
                            isLoading={activeMutation.isPending && activeMutation.variables?.id === user.id}
                            onClick={() => activeMutation.mutate({ id: user.id, isActive: true })}
                          >
                            <UserCheck className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          title={t('common.delete')}
                          onClick={() => setDeleteModal({ open: true, user })}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {/* User Create/Edit Modal */}
      <Modal
        isOpen={userModal.open}
        onClose={() => setUserModal({ open: false, user: null })}
        title={userModal.user ? t('users.editUser') : t('users.newUser')}
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label required>{t('auth.username')}</Label>
            <Input
              value={formData.username}
              onChange={(e) => setFormData((prev) => ({ ...prev, username: e.target.value }))}
              disabled={Boolean(userModal.user)}
            />
          </div>

          {!userModal.user && (
            <div>
              <Label required>{t('auth.password')}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData((prev) => ({ ...prev, password: e.target.value }))}
              />
            </div>
          )}

          <div>
            <Label required>{t('users.fullName')}</Label>
            <Input
              value={formData.fullName}
              onChange={(e) => setFormData((prev) => ({ ...prev, fullName: e.target.value }))}
            />
          </div>

          <div>
            <Label>{t('reports.division')}</Label>
            <Select
              value={formData.division}
              onChange={(e) => setFormData((prev) => ({ ...prev, division: e.target.value }))}
            >
              <option value="">--</option>
              {DIVISIONS.map((div) => (
                <option key={div} value={div}>{div}</option>
              ))}
            </Select>
            {['處長室', '工安組'].includes(formData.division) && (
              <p className="text-xs text-gray-500 mt-1">
                {t('users.hqDivisionHint', '處長室、工安組可查看及管理各級別工程清單，並擁有手動推播權限')}
              </p>
            )}
          </div>

          <div>
            <Label required>{t('users.role')}</Label>
            <Select
              value={formData.role}
              onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
            >
              <option value="section_user">{t('roles.section_user')}</option>
              <option value="section_supervisor">{t('roles.section_supervisor')}</option>
              <option value="contractor">{t('roles.contractor', '承攬商')}</option>
              <option value="admin">{t('roles.admin')}</option>
            </Select>
          </div>

          {formData.role === 'contractor' && (
            <div>
              <Label required>{t('workItems.contractor')}</Label>
              <Select
                value={formData.contractorId || ''}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, contractorId: e.target.value ? Number(e.target.value) : null }))
                }
              >
                <option value="">--</option>
                {contractors?.filter((c) => c.name !== '台電自營').map((c) => (
                  <option key={c.id} value={c.id}>{c.shortName || c.name}</option>
                ))}
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {t('users.contractorHint', '承攬商帳號只可查看及管理自己承攬的工程')}
              </p>
            </div>
          )}

          {formError && <p className="text-sm text-red-600">{formError}</p>}

          <div>
            <Label>{t('users.preferredLanguage')}</Label>
            <Select
              value={formData.preferredLanguage}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, preferredLanguage: e.target.value }))
              }
            >
              <option value="zh-TW">{t('language.zhTW')}</option>
              <option value="en">{t('language.en')}</option>
            </Select>
          </div>

          {formData.role !== 'contractor' && (
            <div>
              <label className="flex items-center gap-2 text-gray-700">
                <input
                  type="checkbox"
                  checked={pushIsBuiltIn(formData.role, formData.division) || formData.canPushLine}
                  disabled={pushIsBuiltIn(formData.role, formData.division)}
                  onChange={(e) => setFormData((prev) => ({ ...prev, canPushLine: e.target.checked }))}
                />
                {t('users.allowLinePush', '允許推播 LINE 訊息')}
              </label>
              <p className="text-xs text-gray-500 mt-1">
                {pushIsBuiltIn(formData.role, formData.division)
                  ? t('users.linePushBuiltIn', '系統管理員、處長室、工安組已內建推播權限')
                  : t('users.linePushHint', '可推播自己部門的出工通報至 LINE 群組')}
              </p>
            </div>
          )}

          {userModal.user && (
            <div>
              <Label>{t('users.isActive')}</Label>
              <Select
                value={formData.isActive ? 'true' : 'false'}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, isActive: e.target.value === 'true' }))
                }
              >
                <option value="true">{t('users.active')}</option>
                <option value="false">{t('users.inactive')}</option>
              </Select>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setUserModal({ open: false, user: null })}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {t('common.save')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={passwordModal.open}
        onClose={() => {
          setPasswordModal({ open: false, user: null });
          setNewPassword('');
        }}
        title={t('users.resetPassword')}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            {passwordModal.user?.username}
          </p>
          <div>
            <Label required>{t('users.newPassword')}</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t('errors.minLength', { min: 6 })}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setPasswordModal({ open: false, user: null });
                setNewPassword('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              isLoading={resetPasswordMutation.isPending}
              onClick={handleResetPassword}
              disabled={newPassword.length < 6}
            >
              {t('users.resetPassword')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Deactivate Confirmation Modal */}
      <Modal
        isOpen={deactivateModal.open}
        onClose={() => setDeactivateModal({ open: false, user: null })}
        title={t('users.deactivate', '停用')}
        size="sm"
      >
        <div className="space-y-4">
          <p>{t('users.deleteConfirm')}</p>
          <p className="text-sm text-gray-500">{deactivateModal.user?.username}（{deactivateModal.user?.fullName}）</p>
          <p className="text-sm text-gray-500">
            {t('users.deactivateHint', '停用後無法登入，資料保留，可隨時重新啟用。')}
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeactivateModal({ open: false, user: null })}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              isLoading={activeMutation.isPending}
              onClick={() => activeMutation.mutate({ id: deactivateModal.user?.id, isActive: false })}
            >
              {t('users.deactivate', '停用')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.open}
        onClose={closeDeleteModal}
        title={t('common.delete')}
        size="sm"
      >
        <div className="space-y-4">
          <p>{t('users.permanentDeleteConfirm', '確定要永久刪除此使用者嗎？')}</p>
          <p className="text-sm text-gray-500">{deleteModal.user?.username}（{deleteModal.user?.fullName}）</p>
          <p className="text-sm text-red-600">
            {t('users.permanentDeleteHint', '刪除後無法復原。此帳號建立的通報、公告及操作紀錄會保留，但建立者將顯示為空白。若只是暫時不使用，請改用「停用」。')}
          </p>
          {actionError && <p className="text-sm text-red-600">{actionError}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={closeDeleteModal}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              isLoading={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate(deleteModal.user?.id)}
            >
              {t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
